// =============================================================================
// Quick Tips & Tones — feature constants + helpers
// =============================================================================
import { applicationUseCaseMapping } from "../library/utils";

// Response type tabs (top-level)
export const RESPONSE_TYPE = Object.freeze({
  MESSAGE: "MESSAGE",
  REVIEW: "REVIEW",
});

export const RESPONSE_TYPE_TABS = [
  { key: RESPONSE_TYPE.MESSAGE, label: "Message Response" },
  { key: RESPONSE_TYPE.REVIEW, label: "Review Response" },
];

// Copy shown in the section header, keyed by active response type
export const RESPONSE_TYPE_COPY = Object.freeze({
  [RESPONSE_TYPE.MESSAGE]: {
    title: "Message Response",
    description:
      "Configure the quick tips and tones your team can pick from when the AI drafts a customer message response.",
    tipsDescription: "Short instructions your team can toggle on when generating a message",
    tonesDescription: "The voice the AI writes in when it replies to a message.",
    additionalNoteDescription:
      "Allow your team to add additional context to generate the response.",
  },
  [RESPONSE_TYPE.REVIEW]: {
    title: "Review Response",
    description:
      "Configure the quick tips and tones the AI uses when it drafts a reply to a social review. ",
    tipsDescription: "Instructions the AI follows when drafting a reply.",
    tonesDescription: "The voice the AI writes in when it replies to a review.",
    additionalNoteDescription: "Allow to add additional note/context to generate the response.",
  },
});

// Channel filter (Segmented)
export const CHANNEL_FILTER = Object.freeze({
  ALL: "All",
  TEXT: "Text",
  EMAIL: "Email",
});

export const CHANNEL_FILTER_OPTIONS = [
  { label: "All", value: CHANNEL_FILTER.ALL },
  { label: "Text (SMS)", value: CHANNEL_FILTER.TEXT },
  { label: "Email", value: CHANNEL_FILTER.EMAIL },
];

// Suggestion section types
export const SUGGESTION_TYPE = Object.freeze({
  TIPS: "QUICK_TIPS",
  TONES: "QUICK_TONES",
  ADDITIONAL_NOTES: "ADDITIONAL_NOTES",
});

// applicationUseCase code per response tab — resolved to an id at runtime via
// getApplicationUseCaseId(applicationUseCases, code).
export const APPLICATION_USE_CASE_CODE = Object.freeze({
  [RESPONSE_TYPE.MESSAGE]: applicationUseCaseMapping.ENGAGEMENT_MESSAGE_SUGGESTION,
  [RESPONSE_TYPE.REVIEW]: applicationUseCaseMapping.REVIEW_RESPONSE,
});

// Tip text stored for the ADDITIONAL_NOTES record
export const ADDITIONAL_NOTES_TIP = "Allow to add additional notes";

// Max characters allowed for a tip/tone text field
export const TIP_TONE_MAX_LENGTH = 40;
export const TIP_TONE_LIMIT_MESSAGE = `You've reached to ${TIP_TONE_MAX_LENGTH}-character limit.`;

// Human label for a suggestion type (used in drawer titles/fields)
export const SUGGESTION_TYPE_LABEL = Object.freeze({
  [SUGGESTION_TYPE.TIPS]: "Quick tip",
  [SUGGESTION_TYPE.TONES]: "Tone",
});

// Short word for a suggestion type (used inline in copy, e.g. "this tip")
export const SUGGESTION_TYPE_WORD = Object.freeze({
  [SUGGESTION_TYPE.TIPS]: "tip",
  [SUGGESTION_TYPE.TONES]: "tone",
});

// Communication channel options for the add/edit form
export const COMMUNICATION_CHANNEL_OPTIONS = [
  { label: "Text", value: "Text", description: "While drafting the text response." },
  { label: "Email", value: "Email", description: "While drafting the email response." },
];

// Scope levels used by the sidebar hierarchy
export const SCOPE_LEVEL = Object.freeze({
  LOCATION: "LOCATION",
  DEPARTMENT: "DEPARTMENT",
  OPPORTUNITY_TYPE: "OPPORTUNITY_TYPE",
});

// Badge shown next to a scope node
export const SCOPE_BADGE = Object.freeze({
  DEFAULT: "Default",
  CUSTOM: "Custom",
});

// A stable id for the location-level scope node
export const LOCATION_SCOPE_ID = "location-default";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Flatten the scope tree into a single list so a node can be resolved by id.
 * @param {object} tree { location, departments[], opportunityTypes[] }
 * @returns {Array}
 */
export const flattenScopeTree = tree => {
  if (!tree) return [];
  return [tree.location, ...(tree.departments || []), ...(tree.opportunityTypes || [])];
};

/**
 * Resolve a scope node by id (falls back to the location node).
 * @param {object} tree
 * @param {string} scopeId
 */
export const findScopeById = (tree, scopeId) =>
  flattenScopeTree(tree).find(node => node?.id === scopeId) || tree?.location;

/**
 * The suggestion-object field name for a given suggestion type.
 * @param {string} type one of SUGGESTION_TYPE
 * @returns {"tips"|"tones"}
 */
export const listKeyForType = type => (type === SUGGESTION_TYPE.TONES ? "tones" : "tips");

/** True when the scope only supports reading suggestions (opportunity type). */
export const isScopeReadOnly = scope => scope?.level === SCOPE_LEVEL.OPPORTUNITY_TYPE;

/** True for a non-location scope (department / opportunity type). */
export const isOverridableScope = scope =>
  scope?.level === SCOPE_LEVEL.DEPARTMENT || scope?.level === SCOPE_LEVEL.OPPORTUNITY_TYPE;

/** Lowercase noun for a scope, used in confirmation copy. */
export const scopeNoun = scope =>
  scope?.level === SCOPE_LEVEL.OPPORTUNITY_TYPE ? "opportunity type" : "department";

/**
 * Distinct opportunity types backing a location's active departments.
 * @param {Array} departments GET_DEPARTMENTS rows (each with opportunityType)
 * @returns {Array<{id:number,name:string}>}
 */
export const extractOpportunityTypes = (departments = []) => {
  const byId = new Map();
  departments.forEach(dept => {
    const ot = dept?.opportunityType;
    if (ot?.id != null && !byId.has(ot.id)) {
      byId.set(ot.id, { id: ot.id, name: ot.name });
    }
  });
  return [...byId.values()];
};

/**
 * Build the sidebar scope tree from live data. Each node carries `refId`
 * (the backend id) and `level` so the query/mutation layer can target it.
 */
export const buildScopeTree = ({ locationName, departments = [], opportunityTypes = [] } = {}) => ({
  location: {
    id: LOCATION_SCOPE_ID,
    level: SCOPE_LEVEL.LOCATION,
    name: locationName || "Location",
    badge: SCOPE_BADGE.DEFAULT,
    refId: null,
  },
  departments: departments.map(dept => ({
    id: `dept-${dept.id}`,
    level: SCOPE_LEVEL.DEPARTMENT,
    name: dept.name,
    badge: null,
    refId: dept.id,
  })),
  opportunityTypes: opportunityTypes.map(ot => ({
    id: `opp-${ot.id}`,
    level: SCOPE_LEVEL.OPPORTUNITY_TYPE,
    name: ot.name,
    badge: null,
    refId: ot.id,
  })),
});

/**
 * TEMPORARY: BE returns `communicationChannels` as a string (e.g.
 * "Text" or "Text,Email") instead of an array. Normalize to an array so the
 * UI can work uniformly. Remove once BE returns an array.
 */
export const normalizeChannels = channels => {
  if (Array.isArray(channels)) return channels;
  if (typeof channels === "string") {
    return channels
      .split(",")
      .map(c => c.trim())
      .filter(Boolean);
  }
  return [];
};

/** Map a GET_AI_SUGGESTION_QUICK_TIP row to the shape the UI renders. */
export const mapSuggestionItem = item => ({
  id: item.id,
  title: item.tip,
  description: item.description || "",
  channels: normalizeChannels(item.communicationChannels),
  enabled: item.isActive,
  type: item.suggestionType,
  order: item.order ?? null,
  opportunityTypeId: item.opportunityTypeId ?? null,
});

/** Ascending comparator by `order`; items without an order fall to the end. */
export const byOrder = (a, b) => (a?.order ?? Infinity) - (b?.order ?? Infinity);

/**
 * Order tips/tones for display: items with a saved `order` come first (ascending
 * by order); items without an order fall back to alphabetical order by their
 * text. Non-mutating.
 *
 * @param {Array}   list      raw tip/tone rows
 * @param {(item)=>string} [getText] text accessor (defaults to `tip`/`title`)
 */
export const sortTipsAndTones = (list = [], getText = it => it?.tip ?? it?.title ?? "") =>
  [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const aHasOrder = a?.order != null;
    const bHasOrder = b?.order != null;
    if (aHasOrder && bHasOrder) return a.order - b.order;
    if (aHasOrder) return -1;
    if (bHasOrder) return 1;
    return String(getText(a)).localeCompare(String(getText(b)), undefined, {
      sensitivity: "base",
    });
  });

/**
 * Split the raw suggestion list into tips / tones / additional-note record.
 * Tips and tones are sorted by their saved `order`.
 * @param {Array} list getAiSuggestionQuickTip rows
 */
export const splitSuggestions = (list = []) => {
  const rows = Array.isArray(list) ? list : [];
  const tips = rows
    .filter(it => it?.suggestionType === SUGGESTION_TYPE.TIPS)
    .map(mapSuggestionItem)
    .sort(byOrder);
  const tones = rows
    .filter(it => it?.suggestionType === SUGGESTION_TYPE.TONES)
    .map(mapSuggestionItem)
    .sort(byOrder);
  const additionalNote = rows.find(it => it?.suggestionType === SUGGESTION_TYPE.ADDITIONAL_NOTES);
  return {
    tips,
    tones,
    additionalNoteId: additionalNote?.id ?? null,
    additionalNote: Boolean(additionalNote?.isActive),
  };
};

/**
 * Variables for GET_AI_SUGGESTION_QUICK_TIP based on the active scope/filters.
 */
export const buildTipsVariables = ({ companyId, locationId, scope, channelFilter, useCaseId }) => {
  const variables = {
    companyId: companyId || undefined,
    locationIds: locationId ? [Number(locationId)] : undefined,
    applicationUseCaseId: useCaseId,
    communicationChannel:
      channelFilter && channelFilter !== CHANNEL_FILTER.ALL ? channelFilter : undefined,
  };
  if (scope?.level === SCOPE_LEVEL.DEPARTMENT && scope.refId != null) {
    variables.departmentIds = [Number(scope.refId)];
  }
  if (scope?.level === SCOPE_LEVEL.OPPORTUNITY_TYPE && scope.refId != null) {
    variables.rootOppTypeId = Number(scope.refId);
  }
  return variables;
};

/**
 * Read the in-payload result of an AI-suggestion mutation. The API returns
 * { <mutationName>: { statusCode, message } } even for action-level failures
 * (e.g. statusCode 400), so a resolved promise isn't necessarily a success.
 */
export const readMutationResult = res => {
  const payload = res?.data ? Object.values(res.data)[0] : null;
  const statusCode = payload?.statusCode;
  return {
    ok: statusCode === 200 || statusCode === 201,
    statusCode,
    message: payload?.message,
  };
};

/** departmentId to persist a create at the given scope (null for location). */
export const departmentIdForScope = scope =>
  scope?.level === SCOPE_LEVEL.DEPARTMENT ? Number(scope.refId) : null;

/** opportunityTypeId to persist a create at the given scope (null unless opp type). */
export const opportunityTypeIdForScope = scope =>
  scope?.level === SCOPE_LEVEL.OPPORTUNITY_TYPE ? Number(scope.refId) : null;

/**
 * Count failures in a Promise.allSettled result of AI-suggestion mutations.
 * A settled entry is a failure when the promise rejected OR the resolved
 * payload reported a non-2xx statusCode.
 * @param {Array<PromiseSettledResult>} settled
 * @returns {number}
 */
export const countMutationFailures = (settled = []) =>
  settled.filter(r => r.status === "rejected" || !readMutationResult(r.value).ok).length;

/**
 * Builds the panel header (title + description) for the selected scope.
 * @param {object} scope        selected scope node ({ level, name })
 * @param {string} locationName currently selected location name
 * @param {string} responseType RESPONSE_TYPE.MESSAGE | RESPONSE_TYPE.REVIEW
 * @returns {{ title: string, description: string }}
 */
export const getScopeHeader = (
  scope,
  locationName = "location",
  responseType = RESPONSE_TYPE.MESSAGE
) => {
  const location = locationName || "location";
  const isReview = responseType === RESPONSE_TYPE.REVIEW;
  // Noun for what the scope applies to, per response type.
  const applyNoun = isReview ? "review reply" : "message";

  switch (scope?.level) {
    case SCOPE_LEVEL.DEPARTMENT:
      return {
        title: `${scope.name} Department`,
        description: `Applies to every ${applyNoun} from the ${scope.name} department at ${location}.`,
      };
    case SCOPE_LEVEL.OPPORTUNITY_TYPE:
      return {
        title: `${scope.name} Opportunity`,
        description: `Applies to every ${applyNoun} from the ${scope.name} opportunity at ${location}.`,
      };
    case SCOPE_LEVEL.LOCATION:
    default:
      return {
        title: location,
        description: isReview
          ? "Location default used for every review reply unless an opportunity type overrides it."
          : "Location default applies everywhere unless a department or opportunity type overrides it.",
      };
  }
};
